import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { walletColumns, WALLETS_TOTALS_ROW_ID } from 'src/constants/payments-ops/walletsGrid'
import type { PartnerWallet } from 'src/interfaces/wallet'
import { getWallets, getWalletsLoading } from 'src/selectors/wallet'
import { addS } from 'src/utils/baseUtils'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { sumWalletBalances } from 'src/utils/paymentsOps/walletTotals'

const getRowId = (row: PartnerWallet): string => {
  return row.partnerId
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No wallets." />
}

const WalletsTableComponent = () => {
  const apiRef = useGridApiRef()
  const wallets = useSelector(getWallets)
  const isLoading = useSelector(getWalletsLoading)
  const { paginationModel, setPaginationModel } = useGridPagination(PAYMENTS_OPS_PAGE_SIZE_OPTIONS)

  // Totals reflect the loaded (filter-applied) rows, across every page.
  const pinnedRows = useMemo(() => {
    const totals = sumWalletBalances(wallets)

    return {
      bottom: [
        {
          partnerId: WALLETS_TOTALS_ROW_ID,
          partnerName: `All partners (${wallets.length} ${addS(wallets.length, 'wallet')})`,
          partnerStatus: '',
          virtualAccountName: '',
          virtualAccountNickName: '',
          virtualAccountStatus: '',
          totalBalance: totals.totalBalance,
          availableBalance: totals.availableBalance,
        },
      ],
    }
  }, [wallets])

  const handleExport = useCallback(() => {
    apiRef.current?.exportDataAsCsv({ fileName: 'partner-wallets', allColumns: true })
  }, [apiRef])

  return (
    <GridTable<PartnerWallet>
      apiRef={apiRef}
      rows={wallets}
      columns={walletColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      pinnedRows={wallets.length > 0 ? pinnedRows : undefined}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      onExportClick={handleExport}
      EmptyElement={renderEmpty}
      tableDataTestId="wallets-table"
      rowDataTestIdPrefix="wallets"
    />
  )
}

export const WalletsTable = memo(WalletsTableComponent)
